/**
 * SaveToProjectDialog Component
 *
 * Dialog for saving selected images to a project.
 * Options:
 * - Save to existing project
 * - Create new project
 */

import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { FolderPlus, Folder, Loader2, Image } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePhotostudioProjects } from '@/hooks/usePhotostudioProjects';
import type { ISaveToProjectDialogProps } from '@/@types/photostudio';
import type { Id } from '../../../convex/_generated/dataModel';

export function SaveToProjectDialog({
  isOpen,
  onClose,
  onSave,
  selectedCount,
  isSaving,
}: ISaveToProjectDialogProps) {
  const { projects, isLoading } = usePhotostudioProjects();
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [selectedProjectId, setSelectedProjectId] = useState<Id<'photostudioProjects'> | null>(
    null
  );
  const [newProjectName, setNewProjectName] = useState('');
  const newProjectInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mode === 'new') {
      const timer = setTimeout(() => newProjectInputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [mode]);

  const canSave = mode === 'new' ? newProjectName.trim().length > 0 : selectedProjectId !== null;

  const handleSave = () => {
    if (mode === 'new') {
      onSave('new', newProjectName.trim());
    } else if (selectedProjectId) {
      onSave(selectedProjectId);
    }
  };

  const handleClose = () => {
    setMode('existing');
    setSelectedProjectId(null);
    setNewProjectName('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Save to Project</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            Save {selectedCount} selected image{selectedCount !== 1 ? 's' : ''} to a project
          </p>

          <RadioGroup
            value={mode}
            onValueChange={(value: string) => setMode(value as 'existing' | 'new')}
            className="space-y-3"
          >
            {/* Existing Project Option */}
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="existing" id="existing" />
              <Label htmlFor="existing" className="text-sm font-medium cursor-pointer">
                Save to existing project
              </Label>
            </div>

            {mode === 'existing' && (
              <div className="ml-6">
                {isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="size-5 animate-spin text-muted-foreground" />
                  </div>
                ) : projects.length === 0 ? (
                  <p className="py-2 text-sm text-muted-foreground">
                    No projects yet. Create a new one below.
                  </p>
                ) : (
                  <ScrollArea className="max-h-[200px]">
                    <div className="space-y-2">
                      {projects.map(project => (
                        <Card
                          key={project._id}
                          className={cn(
                            'flex cursor-pointer items-center gap-3 p-3 transition-all hover:bg-muted/50',
                            selectedProjectId === project._id && 'ring-2 ring-primary'
                          )}
                          onClick={() => setSelectedProjectId(project._id)}
                        >
                          {/* Thumbnail */}
                          <div className="size-10 shrink-0 overflow-hidden rounded-md bg-muted">
                            {project.thumbnailUrl ? (
                              <img
                                src={project.thumbnailUrl}
                                alt={project.name}
                                className="size-full object-cover"
                              />
                            ) : (
                              <div className="flex size-full items-center justify-center">
                                <Folder className="size-5 text-muted-foreground" />
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{project.name}</p>
                            <p className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Image className="size-3" />
                              {project.imageCount} image{project.imageCount !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>
            )}

            {/* New Project Option */}
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="new" id="new" />
              <Label htmlFor="new" className="text-sm font-medium cursor-pointer">
                Create new project
              </Label>
            </div>

            {mode === 'new' && (
              <div className="ml-6 space-y-2">
                <Label htmlFor="project-name" className="text-xs text-muted-foreground">
                  Project Name
                </Label>
                <Input
                  ref={newProjectInputRef}
                  id="project-name"
                  value={newProjectName}
                  onChange={e => setNewProjectName(e.target.value)}
                  placeholder="My New Project"
                  maxLength={100}
                />
              </div>
            )}
          </RadioGroup>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={!canSave || isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Saving...
              </>
            ) : mode === 'new' ? (
              <>
                <FolderPlus className="mr-2 size-4" />
                Create & Save
              </>
            ) : (
              <>
                <Folder className="mr-2 size-4" />
                Save to Project
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
